import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as rpc from "../../../common/jsonRpcRoutes";

/** Show the "taking longer than expected" warning once a request has run this long. */
export const SLOW_REQUEST_MS = 5000;
/** Overall limit for ordinary widget requests; the request is aborted and fails as usual. */
export const REQUEST_TIMEOUT_MS = 20000;
/** Limit for booking and payment requests, whose outcome is unknown if they time out. */
export const COMMIT_REQUEST_TIMEOUT_MS = 60000;

type RouteName = rpc.RpcRouteName;

/**
 * Booking and payment requests. Aborting them would not undo the booking or charge
 * on the server, so they are never aborted on navigation and Back is disabled while
 * they run instead.
 */
const COMMIT_ROUTES: ReadonlySet<RouteName> = new Set<RouteName>([
  "/submitBooking",
  "/freedompay/initialise",
  "/freedompay/finalise",
]);

/** Background polling with its own deadline: left exactly as it was. */
const UNTRACKED_ROUTES: ReadonlySet<RouteName> = new Set<RouteName>(["/checkDeposit", "/getPreorderCacheStatus"]);

/**
 * Requests the guest triggers that change something. While one runs, the current
 * screen's Next-style controls are disabled. Background reads are left out so those
 * controls do not flicker.
 */
const BLOCKING_ROUTES: ReadonlySet<RouteName> = new Set<RouteName>([
  "/createBooking",
  "/verifyPendingBooking",
  "/updateBookingCustomer",
  "/bindReturnUrl",
  "/updateCustomerPreOrderItems",
  "/removeNameItems",
  "/updateDietaryRequirements",
  "/validateConfigurableOptions",
  "/updatePreOrderServiceCharge",
  "/checkVoucherRedeemable",
  "/storeCustomerDetails",
  "/savePreorders",
]);

export function isCommitRoute(route: RouteName): boolean {
  return COMMIT_ROUTES.has(route);
}

/** `isCommitRoute` for a name not yet known to be a declared route, e.g. one read off an error. */
function isCommitRouteName(route: string): boolean {
  for (const commitRoute of COMMIT_ROUTES) {
    if (commitRoute === route) return true;
  }
  return false;
}

export type RpcCaller = <name extends RouteName>(
  route: name,
  input: rpc.RpcInput<name>,
  extraHeaders?: Record<string, string>,
) => Promise<rpc.RpcOutput<name>>;

interface WidgetRequestTracker {
  /** `rpc.call` with the widget's timing, and aborted with the screen it was made on. */
  readonly call: RpcCaller;
  /** A request has been running longer than `SLOW_REQUEST_MS`. */
  readonly isSlow: boolean;
  /** Guest-triggered work is running on the current screen. */
  readonly isScreenBusy: boolean;
  /** A booking or payment request is running. */
  readonly isCommitInFlight: boolean;
  /** Counts a guest action towards `isScreenBusy` until the returned function is called. */
  readonly beginAction: () => () => void;
  /** Aborts the screen's ordinary requests; booking and payment requests carry on. */
  readonly abortScreen: (screen: string) => void;
  /**
   * Whether requests were aborted on the current screen since it was last left,
   * clearing the flag. The server may still have applied them, so a screen holding
   * server-side state reloads it when this is `true`.
   */
  readonly consumeInterruption: () => boolean;
  readonly setScreen: (screen: string) => void;
}

const WidgetRequestContext = createContext<WidgetRequestTracker | null>(null);

/** The widget's request tracker, or `null` outside the `<booking-form>` widget. */
export function useWidgetRequests(): WidgetRequestTracker | null {
  return useContext(WidgetRequestContext);
}

/** The caller to use for API requests: tracked inside the widget, plain `rpc.call` elsewhere. */
export function useRpcCaller(): RpcCaller {
  return useWidgetRequests()?.call ?? rpc.call;
}

/** Registers `screen` as the one new requests and actions belong to. */
export function useRequestScreen(screen: string): void {
  const setScreen = useWidgetRequests()?.setScreen;
  useLayoutEffect(() => {
    setScreen?.(screen);
  }, [setScreen, screen]);
}

// Matched by name rather than `instanceof` so the checks hold wherever `jsonRpcRoutes` is mocked.
export function isAbortedRequest<T>(err: T): boolean {
  return err instanceof Error && err.name === "RpcAbortError";
}

/** A tracked request hit its timeout, so the server may still have applied it. */
export function isTimedOutRequest<T>(err: T): boolean {
  return err instanceof Error && err.name === "RpcTimeoutError";
}

/** A booking or payment request timed out, so whether it went through is unknown. */
export function isUnconfirmedOutcome<T>(err: T): boolean {
  return (
    err instanceof Error &&
    err.name === "RpcTimeoutError" &&
    "route" in err &&
    typeof err.route === "string" &&
    isCommitRouteName(err.route)
  );
}

interface WidgetRequestProviderProps {
  readonly children: ReactNode;
}

export function WidgetRequestProvider({ children }: WidgetRequestProviderProps): React.ReactElement {
  const screenRef = useRef("");
  const controllersRef = useRef(new Map<string, AbortController>());
  const inFlightRef = useRef(new Map<string, number>());
  const interruptedRef = useRef(new Set<string>());
  const [currentScreen, setCurrentScreen] = useState("");
  const [busyByScreen, setBusyByScreen] = useState<Readonly<Record<string, number>>>({});
  const [slowCount, setSlowCount] = useState(0);
  const [commitCount, setCommitCount] = useState(0);

  const adjustBusy = useCallback((target: string, delta: number) => {
    setBusyByScreen((prev) => ({ ...prev, [target]: Math.max(0, (prev[target] ?? 0) + delta) }));
  }, []);

  const signalFor = useCallback((target: string): AbortSignal => {
    let controller = controllersRef.current.get(target);
    if (!controller || controller.signal.aborted) {
      controller = new AbortController();
      controllersRef.current.set(target, controller);
    }
    return controller.signal;
  }, []);

  const setScreen = useCallback((next: string) => {
    screenRef.current = next;
    setCurrentScreen(next);
  }, []);

  const abortScreen = useCallback((target: string) => {
    if ((inFlightRef.current.get(target) ?? 0) > 0) interruptedRef.current.add(target);
    controllersRef.current.get(target)?.abort();
    controllersRef.current.delete(target);
  }, []);

  const consumeInterruption = useCallback(() => interruptedRef.current.delete(screenRef.current), []);

  const beginAction = useCallback(() => {
    const target = screenRef.current;
    adjustBusy(target, 1);
    let ended = false;
    return () => {
      if (ended) return;
      ended = true;
      adjustBusy(target, -1);
    };
  }, [adjustBusy]);

  const call = useCallback<RpcCaller>(
    async (route, input, extraHeaders) => {
      if (UNTRACKED_ROUTES.has(route)) return rpc.call(route, input, extraHeaders);

      const target = screenRef.current;
      const commit = COMMIT_ROUTES.has(route);
      const blocking = commit || BLOCKING_ROUTES.has(route);
      let wasSlow = false;
      const options: rpc.RpcCallOptions = {
        timeoutMs: commit ? COMMIT_REQUEST_TIMEOUT_MS : REQUEST_TIMEOUT_MS,
        slowAfterMs: SLOW_REQUEST_MS,
        onSlow: () => {
          wasSlow = true;
          setSlowCount((n) => n + 1);
        },
        ...(commit ? {} : { signal: signalFor(target) }),
      };

      if (blocking) adjustBusy(target, 1);
      if (commit) setCommitCount((n) => n + 1);
      if (!commit) inFlightRef.current.set(target, (inFlightRef.current.get(target) ?? 0) + 1);
      try {
        return await rpc.call(route, input, extraHeaders, options);
      } finally {
        if (!commit) inFlightRef.current.set(target, (inFlightRef.current.get(target) ?? 1) - 1);
        if (wasSlow) setSlowCount((n) => n - 1);
        if (blocking) adjustBusy(target, -1);
        if (commit) setCommitCount((n) => n - 1);
      }
    },
    [adjustBusy, signalFor],
  );

  const value = useMemo<WidgetRequestTracker>(
    () => ({
      call,
      isSlow: slowCount > 0,
      isScreenBusy: (busyByScreen[currentScreen] ?? 0) > 0,
      isCommitInFlight: commitCount > 0,
      beginAction,
      abortScreen,
      consumeInterruption,
      setScreen,
    }),
    [
      call,
      slowCount,
      busyByScreen,
      currentScreen,
      commitCount,
      beginAction,
      abortScreen,
      consumeInterruption,
      setScreen,
    ],
  );

  return <WidgetRequestContext.Provider value={value}>{children}</WidgetRequestContext.Provider>;
}
